import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { toastErro, msgErro } from '../services/ui';
import { useSort } from '../helpers/useSort';
import { useModalSafeClose } from '../helpers/useModalSafeClose';
import MultiSelectBusca from '../components/MultiSelectBusca';
import SeletorFornecedor from '../components/SeletorFornecedor';
import ModalUltimasVendas from '../components/ModalUltimasVendas';
import './Vendas.css';

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const fmtData = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtNum = (n, dec = 0) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtReais = (n) => 'R$ ' + Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// '2026-08-22' -> '22/08/26' (sem new Date, que desloca por fuso).
const fmtDataCurta = (iso) => { const [a, m, d] = String(iso || '').split('-'); return a ? `${d}/${m}/${a.slice(2)}` : '—'; };
// Escreve a planilha no formato que a importação do parceiro espera: cabeçalho na
// linha 1, dado a partir da 2, data como DATA (não texto), número sem unidade,
// sem mescla, sem total e sem linha em branco.
const baixarExcel = ({ nome, aba, colunas, linhas }) => {
  const cabecalho = colunas.map((c) => c.titulo);
  const matriz = [cabecalho, ...linhas.map((l) => colunas.map((c) => c.valor(l)))];
  const ws = XLSX.utils.aoa_to_sheet(matriz, { cellDates: true });
  colunas.forEach((c, i) => {
    if (!c.formato) return;
    for (let r = 1; r < matriz.length; r++) {
      const cel = ws[XLSX.utils.encode_cell({ r, c: i })];
      if (cel && cel.v != null && cel.v !== '') cel.z = c.formato;
    }
  });
  ws['!cols'] = colunas.map((c) => ({ wch: c.largura || 14 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, aba);
  XLSX.writeFile(wb, nome, { cellDates: true });
};
// '2026-08' -> Date(2026,7,1); usado só onde a coluna é data de verdade.
const dataDoDia = (iso) => { const [a, m, d] = String(iso || '').split('-').map(Number); return a ? new Date(a, m - 1, d || 1) : null; };
const fmtPct = (m) => (m == null ? '—' : `${(m * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`);
const mkpClasse = (m) => (m == null ? '' : m >= 0 ? 'vd-mkp-pos' : 'vd-mkp-neg');
const statusBadge = (s) => (s === 'ruptura' ? 'badge-negativo' : s === 'excedente' ? 'badge-pendente' : s === 'repondo' ? 'badge-repondo' : 'badge-positivo');
const statusLabel = (s) => (s === 'ruptura' ? 'Ruptura' : s === 'excedente' ? 'Excedente' : s === 'repondo' ? 'Repondo' : 'Saudável');

export default function Vendas() {
  const hoje = new Date();
  const [inicio, setInicio] = useState(fmtData(new Date(hoje.getFullYear(), hoje.getMonth(), 1)));
  const [fim, setFim] = useState(fmtData(hoje));
  const [dimensao, setDimensao] = useState('filial');
  const [metrica, setMetrica] = useState('qtd'); // qtd | venda (só na visão por filial)
  const [dados, setDados] = useState(null);
  const [vistoComo, setVistoComo] = useState('filial'); // dimensão dos dados carregados
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [exportando, setExportando] = useState(false);
  const [jaGerou, setJaGerou] = useState(false); // já gerou ao menos uma vez (pra auto-regerar ao trocar a visão)
  const [modalVend, setModalVend] = useState(null); // vendedor aberto no modal
  const [modalProds, setModalProds] = useState(null); // produtos | 'loading'
  const [filOpc, setFilOpc] = useState([]); // opções de filial
  const [filSel, setFilSel] = useState([]); // filiais selecionadas
  const [fornSel, setFornSel] = useState(''); // '' = todos os CNPJs (multi-CNPJ)
  const [vendasSku, setVendasSku] = useState(null); // SKU aberto no modal de últimas vendas

  const sPiv = useSort('total', 'desc');
  const sVen = useSort('venda', 'desc');
  const sMod = useSort('venda', 'desc');
  const sGiro = useSort('metros', 'desc');
  const fecharModal = () => { setModalVend(null); setModalProds(null); };
  const modalSafe = useModalSafeClose(fecharModal);

  useEffect(() => {
    api.get('/vendas/filiais').then((r) => setFilOpc(r.data.map((f) => ({ value: f.codigo, label: f.nome })))).catch(() => {});
  }, []);

  // Trocar a visão (filial/vendedor/giro) ou o fornecedor re-gera, mantendo período e filtros.
  useEffect(() => {
    if (jaGerou) gerar();
  }, [dimensao, fornSel]); // eslint-disable-line

  // Atalhos de período. Os dois servem: mês fechado é o que a análise de reposição
  // costuma pedir; 90 dias corridos é o retrato dos últimos três meses até hoje.
  const usarMesesFechados = (n = 3) => {
    const fimMes = new Date(hoje.getFullYear(), hoje.getMonth(), 0); // último dia do mês passado
    setInicio(fmtData(new Date(fimMes.getFullYear(), fimMes.getMonth() - (n - 1), 1)));
    setFim(fmtData(fimMes));
  };
  const usarDiasCorridos = (n = 90) => {
    setInicio(fmtData(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - (n - 1))));
    setFim(fmtData(hoje));
  };

  const gerar = async () => {
    if (inicio > fim) { setErro('A data inicial não pode ser depois da final.'); return; }
    setCarregando(true); setErro(''); fecharModal();
    try {
      const { data } = await api.get('/vendas', { params: { inicio, fim, dimensao, filiais: filSel.join(','), ...(fornSel && { fornecedor: fornSel }) } });
      setDados(data); setVistoComo(dimensao); setJaGerou(true);
    } catch (e) { setErro(msgErro(e, 'Erro ao gerar o relatório.')); setDados(null); }
    finally { setCarregando(false); }
  };

  const exportarExcel = async () => {
    if (inicio > fim) { toastErro('A data inicial não pode ser depois da final.'); return; }
    setExportando(true);
    try {
      const { data } = await api.get('/vendas/relatorio-semanal', { params: { inicio, fim, filiais: filSel.join(','), ...(fornSel && { fornecedor: fornSel }) } });
      if (!data.linhas.length) { toastErro('Sem vendas no período para exportar.'); return; }
      const rows = data.linhas.map((l) => ({
        Ano: l.ano, Trimestre: l.trimestre, Mês: MESES[l.mes - 1] || l.mes, Semana: `${l.semana} Semana`,
        'Data final da semana': l.data_final.slice(0, 10).split('-').reverse().join('/'),
        'Código SKU': l.sku, Produto: l.produto, Quantidade: l.quantidade,
        'Valor unitário sell-out': l.valor_unitario, 'Valor total sell-out': l.valor_total, 'Preço base (custo)': l.preco_custo,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 6 }, { wch: 9 }, { wch: 10 }, { wch: 10 }, { wch: 16 }, { wch: 14 }, { wch: 34 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'entrada_sellout_semanal');
      XLSX.writeFile(wb, `sellout_semanal_${inicio}_a_${fim}.xlsx`);
    } catch (e) { toastErro(msgErro(e, 'Erro ao exportar.')); }
    finally { setExportando(false); }
  };

  // Exporta a ABA ABERTA no modelo pedido pela representação. Cada aba tem sua
  // base: Giro = saída fiscal; Por filial/vendedor = data do pedido. A base vai no
  // nome do arquivo pra ninguém somar coisas de réguas diferentes sem perceber.
  const REL = {
    giro: {
      rotulo: 'Giro', base: 'saida-fiscal', aba: 'giro',
      colunas: [
        { titulo: 'Mês de referência', valor: (l) => l.mes, largura: 16 },
        { titulo: 'Data da posição', valor: () => hoje, formato: 'dd/mm/yyyy', largura: 15 },
        { titulo: 'Código do fabricante', valor: (l) => l.codFabricante, largura: 18 },
        { titulo: 'Código interno', valor: (l) => l.codpro, largura: 13 },
        { titulo: 'Descrição do produto', valor: (l) => l.descr, largura: 40 },
        { titulo: 'Saída fiscal no mês (m2)', valor: (l) => l.m2, formato: '#,##0.00', largura: 20 },
        { titulo: 'Saída fiscal no mês (R$)', valor: (l) => l.valor, formato: '#,##0.00', largura: 20 },
        { titulo: 'Estoque hoje - todas as lojas (m2)', valor: (l) => l.estoque, formato: '#,##0.00', largura: 28 },
        { titulo: 'Data da última venda', valor: (l) => dataDoDia(l.ultimaVendaData), formato: 'dd/mm/yyyy', largura: 18 },
        { titulo: 'Preço da última venda (R$/m2)', valor: (l) => l.ultimaVendaPreco ?? '', formato: '#,##0.00', largura: 24 },
      ],
    },
    filial: {
      rotulo: 'Por filial', base: 'data-do-pedido', aba: 'por_filial',
      colunas: [
        { titulo: 'Mês de referência', valor: (l) => l.mes, largura: 16 },
        { titulo: 'Código do fabricante', valor: (l) => l.codFabricante, largura: 18 },
        { titulo: 'Código interno', valor: (l) => l.codpro, largura: 13 },
        { titulo: 'Descrição do produto', valor: (l) => l.descr, largura: 40 },
        { titulo: 'Filial', valor: (l) => l.filial, largura: 20 },
        { titulo: 'm2 vendidos', valor: (l) => l.m2, formato: '#,##0.00', largura: 13 },
        { titulo: 'Valor vendido (R$)', valor: (l) => l.valor, formato: '#,##0.00', largura: 17 },
      ],
    },
    vendedor: {
      rotulo: 'Por vendedor', base: 'data-do-pedido', aba: 'por_vendedor',
      colunas: [
        { titulo: 'Mês de referência', valor: (l) => l.mes, largura: 16 },
        { titulo: 'Vendedor', valor: (l) => l.vendedor, largura: 26 },
        { titulo: 'Filial', valor: (l) => l.filial, largura: 20 },
        { titulo: 'm2 vendidos', valor: (l) => l.m2, formato: '#,##0.00', largura: 13 },
        { titulo: 'Valor vendido (R$)', valor: (l) => l.valor, formato: '#,##0.00', largura: 17 },
        { titulo: 'Markup (%)', valor: (l) => l.markup ?? '', formato: '0.0%', largura: 12 },
      ],
    },
  };

  const exportarAba = async () => {
    if (inicio > fim) { toastErro('A data inicial não pode ser depois da final.'); return; }
    const cfg = REL[dimensao];
    setExportando(true);
    try {
      const { data } = await api.get('/vendas/exportar', {
        params: { relatorio: dimensao, inicio, fim, filiais: filSel.join(','), ...(fornSel && { fornecedor: fornSel }) },
      });
      if (!data.linhas.length) { toastErro('Sem dados no período para exportar.'); return; }
      baixarExcel({
        nome: `${cfg.aba}_${cfg.base}_${inicio}_a_${fim}.xlsx`,
        aba: cfg.aba, colunas: cfg.colunas, linhas: data.linhas,
      });
    } catch (e) { toastErro(msgErro(e, 'Erro ao exportar.')); }
    finally { setExportando(false); }
  };

  const abrirVendedor = async (v) => {
    setModalVend(v); setModalProds('loading');
    try {
      const { data } = await api.get(`/vendas/vendedor/${v.codvend}/produtos`, { params: { inicio, fim, filiais: filSel.join(','), ...(fornSel && { fornecedor: fornSel }) } });
      setModalProds(data.produtos);
    } catch (e) { toastErro(msgErro(e)); setModalProds([]); }
  };

  const valorCel = (cell) => (cell ? (metrica === 'qtd' ? cell.qtd : cell.venda) : 0);
  const fmtCel = (v) => (metrica === 'qtd' ? (v ? fmtNum(v, 1) : '—') : (v ? fmtReais(v) : '—'));

  return (
    <div>
      <h4 className="fw-bold mb-1"><i className="bi bi-graph-up me-2" style={{ color: 'var(--primary)' }} />Vendas</h4>
      <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>Suas vendas produto a produto, por filial ou vendedor — em metros e em valor.</p>

      <div className="vd-bar">
        <SeletorFornecedor value={fornSel} onChange={setFornSel} />
        <div className="vd-campo"><label>De</label><input type="date" className="form-control" value={inicio} onChange={(e) => setInicio(e.target.value)} /></div>
        <div className="vd-campo"><label>Até</label><input type="date" className="form-control" value={fim} onChange={(e) => setFim(e.target.value)} /></div>
        <div className="vd-campo vd-atalhos">
          <label>Atalhos</label>
          <div className="vd-atalhos-btns">
            <button type="button" onClick={() => usarMesesFechados(3)}
              title="01 do mês até o último dia — meses completos, sem mês pela metade">3 meses fechados</button>
            <button type="button" onClick={() => usarDiasCorridos(90)}
              title="Os últimos 90 dias corridos, até hoje">90 dias</button>
          </div>
        </div>
        <div className="vd-campo">
          <label>Ver</label>
          <div className="vd-seg">
            <button className={dimensao === 'filial' ? 'ativo' : ''} onClick={() => setDimensao('filial')}>Por filial</button>
            <button className={dimensao === 'vendedor' ? 'ativo' : ''} onClick={() => setDimensao('vendedor')}>Por vendedor</button>
            <button className={dimensao === 'giro' ? 'ativo' : ''} onClick={() => setDimensao('giro')}>Giro</button>
          </div>
        </div>
        <div className="vd-campo" style={{ minWidth: 190 }}>
          <label>Filial <span className="text-muted fw-normal">(todas)</span></label>
          <MultiSelectBusca options={filOpc} values={filSel} onChange={setFilSel} placeholder="Todas as filiais" icon="bi-shop"
            cor={{ tagBg: '#e7f1ea', tagFg: '#1f6a2d', tagBorder: '#bcd9c6', focusBorder: '#1f6a2d' }} />
        </div>
        <div className="vd-campo"><label>&nbsp;</label><button className="btn-primary-for" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button></div>
        <div className="vd-campo vd-exports" style={{ marginLeft: 'auto' }}>
          <label>&nbsp;</label>
          <div className="vd-exports-btns">
            <button className="btn-primary-for" onClick={exportarAba} disabled={exportando}
              title={`Exporta a aba "${REL[dimensao].rotulo}" como está na tela — base: ${REL[dimensao].base === 'saida-fiscal' ? 'saída fiscal' : 'data do pedido'}`}>
              <i className="bi bi-download me-1" />{exportando ? 'Exportando...' : `Exportar "${REL[dimensao].rotulo}"`}
            </button>
            <button className="btn-outline-for" onClick={exportarExcel} disabled={exportando}
              title="Sell-out semanal por SKU — formato fixo, não muda com a aba">
              <i className="bi bi-file-earmark-excel me-1" />Modelo 1 — sell-out
            </button>
          </div>
        </div>
      </div>

      {erro && <div className="text-danger mb-3" style={{ fontSize: '0.88rem' }}><i className="bi bi-exclamation-triangle me-1" />{erro}</div>}

      {carregando ? (
        <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} role="status" /></div>
      ) : !dados ? (
        <div className="text-center text-muted py-5">Escolha o período e clique em <b>Gerar</b>.</div>
      ) : (vistoComo === 'vendedor' ? dados.vendedores.length === 0 : dados.produtos.length === 0) ? (
        <div className="text-center text-muted py-4">Nenhuma venda no período.</div>
      ) : vistoComo === 'filial' ? (
        <>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <div style={{ fontSize: '0.85rem', color: 'var(--text-suave)' }}>
              {dados.total} produtos · <b style={{ color: 'var(--text)' }}>{fmtNum(dados.totais.qtd, 1)}</b> un · <b style={{ color: 'var(--text)' }}>{fmtReais(dados.totais.venda)}</b>
              <span className="text-muted" title="Venda pela ótica do pedido (data do pedido). A aba Giro usa a saída fiscal — pode divergir."> · venda pela data do pedido</span>
            </div>
            <div className="vd-seg">
              <button className={metrica === 'qtd' ? 'ativo' : ''} onClick={() => setMetrica('qtd')}>Metros</button>
              <button className={metrica === 'venda' ? 'ativo' : ''} onClick={() => setMetrica('venda')}>Valor</button>
            </div>
          </div>

          <div className="vd-pivot-wrap">
            <table className="vd-pivot">
              <thead>
                <tr>
                  <th className="esq" onClick={() => sPiv.toggle('nome')}>Produto {sPiv.icon('nome')}</th>
                  {dados.colunas.map((c) => <th key={c.chave} title={c.chave} onClick={() => sPiv.toggle(c.chave)}>{c.rotulo} {sPiv.icon(c.chave)}</th>)}
                  <th className="tot" onClick={() => sPiv.toggle('total')}>Total {sPiv.icon('total')}</th>
                </tr>
              </thead>
              <tbody>
                {sPiv.aplicar(dados.produtos, (r, k) => (k === 'nome' ? r.nome : k === 'total' ? (metrica === 'qtd' ? r.totalQtd : r.totalVenda) : valorCel(r.cells[k]))).map((p) => (
                  <tr key={p.codpro}>
                    <td className="esq prod">{p.nome}<br /><small>#{p.codpro} · {p.unid}</small></td>
                    {dados.colunas.map((c) => { const v = valorCel(p.cells[c.chave]); return <td key={c.chave} className={v > 0 ? '' : 'zero'}>{fmtCel(v)}</td>; })}
                    <td className="tot">{fmtCel(metrica === 'qtd' ? p.totalQtd : p.totalVenda)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="esq">Total</td>
                  {dados.colunas.map((c) => { const t = dados.totais.porColuna[c.chave]; const v = t ? (metrica === 'qtd' ? t.qtd : t.venda) : 0; return <td key={c.chave}>{fmtCel(v)}</td>; })}
                  <td className="tot">{fmtCel(metrica === 'qtd' ? dados.totais.qtd : dados.totais.venda)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : vistoComo === 'vendedor' ? (
        <>
          <div className="mb-2" style={{ fontSize: '0.85rem', color: 'var(--text-suave)' }}>
            {dados.total} vendedores · <b style={{ color: 'var(--text)' }}>{fmtNum(dados.totais.qtd, 1)}</b> un · <b style={{ color: 'var(--text)' }}>{fmtReais(dados.totais.venda)}</b>
            <span className="text-muted" title="Venda pela ótica do pedido (data do pedido). A aba Giro usa a saída fiscal — pode divergir."> · venda pela data do pedido · clique num vendedor pra ver os produtos</span>
          </div>
          <div className="vd-pivot-wrap">
            <table className="vd-pivot">
              <thead>
                <tr>
                  <th className="esq" onClick={() => sVen.toggle('nome')}>Vendedor {sVen.icon('nome')}</th>
                  <th style={{ textAlign: 'left' }} onClick={() => sVen.toggle('filial_nome')}>Filial {sVen.icon('filial_nome')}</th>
                  <th onClick={() => sVen.toggle('qtd')}>Metros {sVen.icon('qtd')}</th>
                  <th onClick={() => sVen.toggle('venda')}>Venda {sVen.icon('venda')}</th>
                  <th className="tot" onClick={() => sVen.toggle('markup')}>MKP {sVen.icon('markup')}</th>
                </tr>
              </thead>
              <tbody>
                {sVen.aplicar(dados.vendedores).map((v) => (
                  <tr key={v.codvend} className="linha-clicavel" onClick={() => abrirVendedor(v)} title="Ver produtos deste vendedor">
                    <td className="esq prod">{v.nome} <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.7rem', color: 'var(--muted)' }} /></td>
                    <td style={{ textAlign: 'left' }}>{v.filial_nome}</td>
                    <td>{fmtNum(v.qtd, 1)}</td>
                    <td>{fmtReais(v.venda)}</td>
                    <td className={`mkp-col ${mkpClasse(v.markup)}`}>{fmtPct(v.markup)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td className="esq">Total</td><td className="text-muted">—</td><td>{fmtNum(dados.totais.qtd, 1)}</td><td>{fmtReais(dados.totais.venda)}</td><td className="tot">—</td></tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : (
        <>
          {/* Como o Giro conta — uma linha fechada; abre com as regras, uma por quadrinho */}
          <details className="vd-giro-card mb-2">
            <summary>
              <i className="bi bi-info-circle-fill" />
              <span><b>Como o Giro conta:</b> venda = nota emitida, na loja de onde saiu · estoque com trânsito · cobertura pelos últimos 90 dias</span>
              <span className="vd-giro-card-abrir">Entenda</span>
            </summary>
            <div className="vd-giro-card-grade">
              <div><i className="bi bi-receipt" /><b>Venda = saída fiscal</b>A nota emitida, pela data dela: o que saiu do estoque de verdade. <em>Por filial</em> e <em>Por vendedor</em> contam pelo pedido, por isso os totais podem diferir.</div>
              <div><i className="bi bi-shop" /><b>Loja = quem faturou</b>A venda fica na loja que emitiu a nota, de onde a mercadoria saiu. Quando o Centro de Distribuição entrega a venda de uma loja, a saída é do CD.</div>
              <div><i className="bi bi-box-seam" /><b>Estoque de hoje</b>Disponível nas lojas, somando o que está em trânsito entre elas.</div>
              <div><i className="bi bi-hourglass-split" /><b>Cobertura</b>Meses que o estoque dura no ritmo dos últimos 90 dias ({dados.coberturaJanela.inicio.split('-').reverse().join('/')} a {dados.coberturaJanela.fim.split('-').reverse().join('/')}), seja qual for o período acima.</div>
              <div><i className="bi bi-truck" /><b>A chegar</b>Pedidos de compra a caminho. Em ruptura, mas com pedido a caminho, o status vira <em>Repondo</em>.</div>
              <div><i className="bi bi-clock-history" /><b>Última venda</b>Data do pedido e preço negociado do produto (sem o desconto dado no total do pedido).</div>
            </div>
          </details>
          <div className="mb-2" style={{ fontSize: '0.85rem', color: 'var(--text-suave)' }}>
            {dados.total} produtos · saída fiscal no período <b style={{ color: 'var(--text)' }}>{fmtNum(dados.totais.metros, 1)}</b> m² / <b style={{ color: 'var(--text)' }}>{fmtReais(dados.totais.valor)}</b> · estoque de hoje <b style={{ color: 'var(--text)' }}>{fmtNum(dados.totais.estoque, 1)}</b> m²{dados.totais.aChegar > 0 && <> · a chegar <b style={{ color: 'var(--text)' }}>{fmtNum(dados.totais.aChegar, 1)}</b> m²</>}
          </div>
          <div className="vd-pivot-wrap">
            <table className="vd-pivot">
              <thead>
                <tr>
                  <th className="esq" onClick={() => sGiro.toggle('nome')}>Produto {sGiro.icon('nome')}</th>
                  <th onClick={() => sGiro.toggle('metros')} title="Saída fiscal — nota emitida no período (data de emissão)">Saída no período (m²) {sGiro.icon('metros')}</th>
                  <th onClick={() => sGiro.toggle('valor')} title="Saída fiscal — nota emitida no período (data de emissão)">Saída no período (R$) {sGiro.icon('valor')}</th>
                  <th onClick={() => sGiro.toggle('estoque')} title="Estoque de hoje nas lojas, somando o que está em trânsito entre elas">Estoque hoje (m²) {sGiro.icon('estoque')}</th>
                  <th onClick={() => sGiro.toggle('aChegar')} title="Pedidos de compra ao fornecedor ainda não recebidos (a caminho)">A chegar (m²) {sGiro.icon('aChegar')}</th>
                  <th onClick={() => sGiro.toggle('ultimaVenda')} title="Data do pedido e preço negociado da última venda (no grupo, ou nas lojas filtradas). Clique para ver as últimas vendas">Última venda {sGiro.icon('ultimaVenda')}</th>
                  <th onClick={() => sGiro.toggle('cobertura')} title="Estoque de hoje ÷ saída fiscal média dos últimos 90 dias">Cobertura (meses) <i className="bi bi-info-circle" style={{ fontSize: '0.75rem', opacity: 0.7 }} /> {sGiro.icon('cobertura')}</th>
                  <th style={{ textAlign: 'center' }} onClick={() => sGiro.toggle('status')}>Status {sGiro.icon('status')}</th>
                </tr>
              </thead>
              <tbody>
                {sGiro.aplicar(dados.produtos, (r, k) => (k === 'cobertura' ? (r.cobertura == null ? Infinity : r.cobertura) : k === 'ultimaVenda' ? (r.ultimaVenda?.data || '') : r[k])).map((p) => (
                  <tr key={p.codpro}>
                    <td className="esq prod">{p.nome}<br /><small>#{p.codpro} · {p.unid}</small></td>
                    <td>{fmtNum(p.metros, 1)}</td>
                    <td>{fmtReais(p.valor)}</td>
                    <td>{fmtNum(p.estoque, 1)}</td>
                    <td>{p.aChegar > 0 ? (<>{fmtNum(p.aChegar, 1)}{p.prevChegada && <><br /><small style={{ color: '#9aa89c', fontWeight: 400 }}>chega ~{p.prevChegada}</small></>}</>) : <span className="zero">—</span>}</td>
                    <td className="vd-uv">
                      {p.ultimaVenda ? (
                        <button type="button" className="vd-uv-btn" title="Ver as últimas vendas deste produto"
                          onClick={() => setVendasSku({ codpro: p.codpro, nome: p.nome, unid: p.unid })}>
                          {fmtDataCurta(p.ultimaVenda.data)}{p.ultimaVenda.preco > 0 && <span className="vd-uv-preco"> · {fmtReais(p.ultimaVenda.preco)}</span>}
                        </button>
                      ) : <span className="zero">—</span>}
                    </td>
                    <td>{p.cobertura == null ? '—' : fmtNum(p.cobertura, 1)}</td>
                    <td style={{ textAlign: 'center' }}><span className={statusBadge(p.status)}>{statusLabel(p.status)}</span></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="esq">Total</td>
                  <td>{fmtNum(dados.totais.metros, 1)}</td>
                  <td>{fmtReais(dados.totais.valor)}</td>
                  <td>{fmtNum(dados.totais.estoque, 1)}</td>
                  <td>{dados.totais.aChegar > 0 ? fmtNum(dados.totais.aChegar, 1) : '—'}</td>
                  <td></td><td></td><td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {vendasSku && (
        <ModalUltimasVendas codpro={vendasSku.codpro} nome={vendasSku.nome} unid={vendasSku.unid}
          filiais={filSel} onFechar={() => setVendasSku(null)} />
      )}

      {modalVend && (
        <div className="vd-modal-overlay" {...modalSafe.overlayProps}>
          <div className="vd-modal" {...modalSafe.contentProps}>
            <div className="vd-modal-head">
              <div>
                <div className="fw-bold" style={{ fontSize: '1.02rem' }}>{modalVend.nome}</div>
                <div className="text-muted" style={{ fontSize: '0.82rem', marginTop: 2 }}>
                  #{modalVend.codvend} · {fmtNum(modalVend.qtd, 1)} un · <b style={{ color: 'var(--text)' }}>{fmtReais(modalVend.venda)}</b> · MKP <span className={mkpClasse(modalVend.markup)}>{fmtPct(modalVend.markup)}</span>
                </div>
              </div>
              <button className="vd-modal-fechar" onClick={fecharModal} aria-label="Fechar"><i className="bi bi-x-lg" /></button>
            </div>
            <div className="vd-modal-body">
              {modalProds === 'loading' ? (
                <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} /></div>
              ) : !modalProds || modalProds.length === 0 ? (
                <div className="text-center text-muted py-5">Sem produtos no período.</div>
              ) : (
                <table className="table-for mb-0" style={{ borderRadius: 0, boxShadow: 'none' }}>
                  <thead>
                    <tr>
                      <th onClick={() => sMod.toggle('nome')}>Produto {sMod.icon('nome')}</th>
                      <th className="text-end" onClick={() => sMod.toggle('qtd')}>Metros {sMod.icon('qtd')}</th>
                      <th className="text-end" onClick={() => sMod.toggle('venda')}>Venda {sMod.icon('venda')}</th>
                      <th className="text-end" onClick={() => sMod.toggle('markup')}>MKP {sMod.icon('markup')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sMod.aplicar(modalProds).map((p) => (
                      <tr key={p.codpro}>
                        <td>{p.nome}<br /><small className="text-muted">#{p.codpro} · {p.unid}</small></td>
                        <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNum(p.qtd, 1)}</td>
                        <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtReais(p.venda)}</td>
                        <td className={`text-end ${mkpClasse(p.markup)}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtPct(p.markup)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
